import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';

import {
    type GrowthCompanyGoal,
    type GrowthCompanyGoalUpsert,
    type GrowthCreateGoalRequest,
    type GrowthGoal,
    type GrowthGoalActionRequest,
    type GrowthRecommendations,
    growthCompanyGoalSchema,
    growthGoalSchema,
    growthRecommendationsSchema,
} from '../../data-contract/growth';
import { growthKeys, growthPathnames } from './growth-queries';

export const useCreateGoal = (): UseMutationResult<GrowthGoal, Error, GrowthCreateGoalRequest> => {
    const { validatingCobrainerApiFetcher } = useApplicationContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const result = await validatingCobrainerApiFetcher.postJson(growthGoalSchema, {
                pathname: growthPathnames.goals,
                payload,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: () =>
            queryClient.invalidateQueries({
                // Settled suggestions drop out of the backend list; keep the cached set so wizard cards keep their status.
                predicate: ({ queryKey }) => queryKey[0] === growthKeys.all[0] && queryKey[1] !== 'recommendations',
            }),
    });
};

export const useGoalAction = (goalId: string): UseMutationResult<GrowthGoal, Error, GrowthGoalActionRequest> => {
    const { validatingCobrainerApiFetcher } = useApplicationContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload) => {
            const result = await validatingCobrainerApiFetcher.postJson(growthGoalSchema, {
                pathname: growthPathnames.goalActions(goalId),
                payload,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: growthKeys.all }),
    });
};

export const useSaveCompanyGoal = (): UseMutationResult<
    GrowthCompanyGoal,
    Error,
    GrowthCompanyGoalUpsert & { readonly id?: string }
> => {
    const { validatingCobrainerApiFetcher } = useApplicationContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, ...payload }) => {
            const result =
                id === undefined
                    ? await validatingCobrainerApiFetcher.postJson(growthCompanyGoalSchema, {
                          pathname: growthPathnames.companyGoals,
                          payload,
                      })
                    : await validatingCobrainerApiFetcher.patchJson(growthCompanyGoalSchema, {
                          pathname: growthPathnames.companyGoal(id),
                          payload,
                      });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: growthKeys.all }),
    });
};

export const useDeleteCompanyGoal = (): UseMutationResult<void, Error, string> => {
    const { cobrainerApiFetcher } = useApplicationContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id) => {
            const result = await cobrainerApiFetcher.delete({ pathname: growthPathnames.companyGoal(id) });

            if (!result.isOk) {
                throw result.error;
            }
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: growthKeys.all }),
    });
};

export const useRegenerateRecommendations = (): UseMutationResult<GrowthRecommendations, Error, void> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useApplicationContext();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const result = await validatingCobrainerApiFetcher.postJson(growthRecommendationsSchema, {
                pathname: growthPathnames.regenerateRecommendations,
                payload: {},
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (data) => queryClient.setQueryData(growthKeys.recommendations(language.code), data),
    });
};
